/**
 * @module users ui
 */
import { createReducer, AnyAction } from '@reduxjs/toolkit';
import { userActions } from '~/behaviors/actions/user.actions';
import { uiActions } from '~/behaviors/actions/ui.actions';
import { metaActions } from '~/behaviors/actions/meta.actions';
import { employeeAttributeActions } from '~/behaviors/actions/employee_attribute.actions';
import { employeeActions } from '~/behaviors/actions/employee.actions';
import { statuses } from '~/config/settings/user.settings';
import { convert } from '~/tools/convert';

export interface FilterBy {
  status: string;
  lang: string | null;
  userRoleId: number | null;
}

export interface UsersUIState {
  filterBy: FilterBy;
  orderBy: string;
  order: string;
  page: number;
  per: number;
  isLoadingUsers: boolean;
  isLoadingUsersWithPermissions: boolean;
  isCreatingUsers: boolean;
  isUpdatingUsers: boolean;
  isLoadingEmployees: boolean;
  isLoadingEmployeeAttributes: boolean;
  isLoadingEmployeeAttributeValues: boolean;
}

const defaultState: UsersUIState = {
  filterBy: {
    status: statuses.active,
    lang: null,
    userRoleId: null,
  },
  orderBy: 'name',
  order: 'asc',
  page: 0,
  per: 25,
  isLoadingUsers: false,
  isLoadingUsersWithPermissions: false,
  isCreatingUsers: false,
  isUpdatingUsers: false,
  isLoadingEmployees: false,
  isLoadingEmployeeAttributes: false,
  isLoadingEmployeeAttributeValues: false,
};

const reset = (): UsersUIState => defaultState;

const merge =
  (updates: Partial<UsersUIState>) =>
  (state: UsersUIState): UsersUIState => ({
    ...state,
    ...updates,
  });

const changeUI = (state: UsersUIState, action: AnyAction): UsersUIState => {
  const updates = action?.payload || {};
  const sanitizedFilters = convert.pick(updates.filterBy, Object.keys(defaultState.filterBy));
  const sanitizedAttrs = convert.pick(updates, Object.keys(defaultState));

  return {
    ...state,
    ...sanitizedAttrs,
    filterBy: {
      ...state.filterBy,
      ...sanitizedFilters,
    },
  };
};

const usersUI = createReducer(defaultState, (builder) => {
  builder
    .addCase(metaActions.reset, reset)
    .addCase(uiActions.changeUsersUI, changeUI)
    .addCase(userActions.fetchUsers.pending, merge({ isLoadingUsers: true }))
    .addCase(userActions.fetchUsers.fulfilled, merge({ isLoadingUsers: false }))
    .addCase(userActions.fetchUsers.rejected, merge({ isLoadingUsers: false }))
    .addCase(userActions.fetchUsersWithPermissions.pending, merge({ isLoadingUsersWithPermissions: true }))
    .addCase(userActions.fetchUsersWithPermissions.fulfilled, merge({ isLoadingUsersWithPermissions: false }))
    .addCase(userActions.fetchUsersWithPermissions.rejected, merge({ isLoadingUsersWithPermissions: false }))
    .addCase(userActions.createUsers.pending, merge({ isCreatingUsers: true }))
    .addCase(userActions.createUsers.fulfilled, merge({ isCreatingUsers: false }))
    .addCase(userActions.createUsers.rejected, merge({ isCreatingUsers: false }))
    .addCase(userActions.modifyUserPermissions.pending, merge({ isUpdatingUsers: true }))
    .addCase(userActions.modifyUserPermissions.fulfilled, merge({ isUpdatingUsers: false }))
    .addCase(userActions.modifyUserPermissions.rejected, merge({ isUpdatingUsers: false }))
    .addCase(employeeActions.fetchEmployees.pending, merge({ isLoadingEmployees: true }))
    .addCase(employeeActions.fetchEmployees.fulfilled, merge({ isLoadingEmployees: false }))
    .addCase(employeeActions.fetchEmployees.rejected, merge({ isLoadingEmployees: false }))
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.pending, merge({ isLoadingEmployeeAttributes: true }))
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.fulfilled, merge({ isLoadingEmployeeAttributes: false }))
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.rejected, merge({ isLoadingEmployeeAttributes: false }))
    .addCase(
      employeeAttributeActions.fetchEmployeeAttributeValues.pending,
      merge({ isLoadingEmployeeAttributeValues: true }),
    )
    .addCase(
      employeeAttributeActions.fetchEmployeeAttributeValues.fulfilled,
      merge({ isLoadingEmployeeAttributeValues: false }),
    )
    .addCase(
      employeeAttributeActions.fetchEmployeeAttributeValues.rejected,
      merge({ isLoadingEmployeeAttributeValues: false }),
    );
});

export default usersUI;
